import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, gdprService } from '../services/api';
import { useToast } from './ToastContext';

const AuthContext = createContext(null);

/**
 * Provider per l'autenticazione
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Verifica se l'utente è autenticato al mount
  useEffect(() => {
    checkAuth();
  }, []);

  /**
   * Verifica lo stato di autenticazione
   */
  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.getMe();
      setUser(response.data.user);
    } catch (error) {
      // Token non valido, rimuovilo
      localStorage.removeItem('accessToken');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login
   */
  const login = useCallback(async (email, password) => {
    console.log('=== DEBUG AuthContext login ===');
    console.log('Email received:', email);

    try {
      const response = await authService.login(email, password);
      console.log('Login response:', response);

      // Se richiede 2FA
      if (response.data.requires2FA) {
        return {
          requires2FA: true,
          userId: response.data.userId,
        };
      }

      // Login completato
      const { accessToken, user } = response.data;
      localStorage.setItem('accessToken', accessToken);
      setUser(user);

      toast.success('Benvenuto!', `Ciao ${user.firstName}`);

      return { success: true };
    } catch (error) {
      console.log('=== DEBUG AuthContext login ERROR ===');
      console.log('Error caught:', error);
      const message = error.response?.data?.message || 'Errore durante il login';
      toast.error('Errore login', message);
      throw error;
    }
  }, [toast]);

  /**
   * Verifica codice 2FA
   */
  const verify2FA = useCallback(async (userId, code) => {
    try {
      const response = await authService.verify2FA(userId, code);
      const { accessToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);
      setUser(user);

      toast.success('Benvenuto!', `Ciao ${user.firstName}`);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Codice non valido';
      toast.error('Errore verifica', message);
      throw error;
    }
  }, [toast]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignora errori di logout
    } finally {
      localStorage.removeItem('accessToken');
      setUser(null);
      toast.info('Arrivederci', 'Logout effettuato con successo');
    }
  }, [toast]);

  /**
   * Aggiorna profilo utente
   */
  const updateProfile = useCallback(async (data) => {
    try {
      const response = await authService.updateMe(data);
      setUser(response.data.user);
      toast.success('Profilo aggiornato', 'Le modifiche sono state salvate');
      return response.data.user;
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante l\'aggiornamento';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  /**
   * Cambia password
   */
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword, confirmPassword);
      toast.success('Password cambiata', 'La password è stata aggiornata con successo');
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante il cambio password';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  /**
   * Abilita 2FA
   */
  const enable2FA = useCallback(async () => {
    try {
      await authService.enable2FA();
      setUser(prev => ({ ...prev, twoFactorEnabled: true }));
      toast.success('2FA abilitato', 'L\'autenticazione a due fattori è stata attivata');
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante l\'attivazione';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  /**
   * Disabilita 2FA
   */
  const disable2FA = useCallback(async (password) => {
    try {
      await authService.disable2FA(password);
      setUser(prev => ({ ...prev, twoFactorEnabled: false }));
      toast.success('2FA disabilitato', 'L\'autenticazione a due fattori è stata disattivata');
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante la disattivazione';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  /**
   * Accetta informativa privacy (GDPR)
   */
  const acceptPrivacy = useCallback(async () => {
    try {
      const response = await gdprService.acceptPrivacy();
      setUser(response.data.user);
      toast.success('Privacy accettata', 'Grazie per aver accettato l\'informativa sulla privacy');
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante l\'accettazione';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  /**
   * Cancella account (GDPR)
   */
  const deleteAccount = useCallback(async (password) => {
    try {
      await gdprService.deleteAccount(password);
      // Logout dopo eliminazione
      localStorage.removeItem('accessToken');
      setUser(null);
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante l\'eliminazione';
      toast.error('Errore', message);
      throw error;
    }
  }, [toast]);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    verify2FA,
    logout,
    updateProfile,
    changePassword,
    enable2FA,
    disable2FA,
    checkAuth,
    acceptPrivacy,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook per usare l'autenticazione
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato dentro un AuthProvider');
  }
  return context;
}
